
import React, { useEffect, useRef, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PaymentFormData } from '@/types/PaymentFormData';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/providers/AuthProvider';

interface CustomerDetailsFormProps {
  form: UseFormReturn<PaymentFormData>;
  includeAddress: boolean;
  onIncludeAddressChange: (checked: boolean) => void;
  afterPhoneSlot?: React.ReactNode;
}

interface CustomerSuggestion {
  id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  postcode: string | null;
}

const CustomerDetailsForm = ({ form, includeAddress, onIncludeAddressChange, afterPhoneSlot }: CustomerDetailsFormProps) => {
  const { user } = useAuth();
  const [suggestions, setSuggestions] = useState<CustomerSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const skipNextQueryRef = useRef(false);
  const blurTimerRef = useRef<number | null>(null);

  const nameValue = form.watch('customerName') || '';

  useEffect(() => {
    if (skipNextQueryRef.current) {
      skipNextQueryRef.current = false;
      return;
    }
    if (!user?.id) return;
    const query = nameValue.trim();
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }
    const handle = window.setTimeout(async () => {
      const { data, error } = await supabase
        .from('customers')
        .select('id,name,email,phone,address,city,postcode')
        .eq('user_id', user.id)
        .ilike('name', `${query}%`)
        .order('updated_at', { ascending: false })
        .limit(6);
      if (!error && data) {
        setSuggestions(data as CustomerSuggestion[]);
        setShowSuggestions(true);
        setActiveIndex(-1);
      }
    }, 200);
    return () => window.clearTimeout(handle);
  }, [nameValue, user?.id]);

  const selectSuggestion = (s: CustomerSuggestion) => {
    skipNextQueryRef.current = true;
    form.setValue('customerName', s.name || '', { shouldValidate: true, shouldDirty: true });
    if (s.email) form.setValue('customerEmail', s.email, { shouldValidate: true, shouldDirty: true });
    if (s.phone) form.setValue('customerPhone', s.phone, { shouldValidate: true, shouldDirty: true });
    if (s.address || s.city || s.postcode) {
      if (!includeAddress) onIncludeAddressChange(true);
      if (s.address) form.setValue('street', s.address, { shouldDirty: true });
      if (s.city) form.setValue('city', s.city, { shouldDirty: true });
      if (s.postcode) form.setValue('postcode', s.postcode, { shouldDirty: true });
    }
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      selectSuggestion(suggestions[activeIndex]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Customer Details</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label htmlFor="customerName">Customer Name *</Label>
          <div className="relative">
            <Input
              id="customerName"
              {...form.register('customerName')}
              placeholder="John Smith"
              autoComplete="off"
              onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
              onBlur={() => {
                blurTimerRef.current = window.setTimeout(() => setShowSuggestions(false), 150);
              }}
              onKeyDown={handleKeyDown}
            />
            {showSuggestions && suggestions.length > 0 && (
              <ul
                className="absolute z-50 mt-1 w-full rounded-md border bg-popover text-popover-foreground shadow-md max-h-64 overflow-auto"
                onMouseDown={(e) => {
                  e.preventDefault();
                  if (blurTimerRef.current) window.clearTimeout(blurTimerRef.current);
                }}
              >
                {suggestions.map((s, idx) => (
                  <li
                    key={s.id}
                    className={`px-3 py-2 cursor-pointer text-sm ${idx === activeIndex ? 'bg-accent text-accent-foreground' : 'hover:bg-accent hover:text-accent-foreground'}`}
                    onClick={() => selectSuggestion(s)}
                  >
                    <div className="font-medium">{s.name || 'Unnamed'}</div>
                    {(s.email || s.phone) && (
                      <div className="text-xs text-muted-foreground truncate">
                        {s.email || s.phone}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {form.formState.errors.customerName && (
            <p className="text-sm text-red-500 mt-1">{form.formState.errors.customerName.message as string}</p>
          )}
        </div>
        
        <div>
          <Label htmlFor="customerEmail">Customer Email</Label>
          <Input
            id="customerEmail"
            type="email"
            {...form.register('customerEmail')}
            placeholder="customer@example.com"
          />
          {form.formState.errors.customerEmail && (
            <p className="text-sm text-red-500 mt-1">{form.formState.errors.customerEmail.message as string}</p>
          )}
        </div>

        <div>
          <Label htmlFor="customerPhone">Customer Phone (UK)</Label>
          <Input
            id="customerPhone"
            type="tel"
            {...form.register('customerPhone')}
            placeholder="07123 456789"
          />
          <p className="text-xs text-muted-foreground mt-1">
            At least one of email or phone is required
          </p>
        </div>

        {afterPhoneSlot}

      </CardContent>
    </Card>
  );
};

export default CustomerDetailsForm;
